import { useMemo, useState } from "react";
import { messages } from "../../i18n/messages.js";
import { AppButton } from "../ui/AppButton.js";

const TG50_CODE = "TG50";
const TG50_MIN = 500;
const TG50_VALUE = 50;

export interface PromoCodePanelProps {
  subtotal: number;
  disabled?: boolean;
  appliedCode: string | null;
  onApply: (code: string) => Promise<{ ok: boolean; message: string; discount: number }>;
  onRemove: () => void;
}

export function PromoCodePanel({
  subtotal,
  disabled = false,
  appliedCode,
  onApply,
  onRemove,
}: PromoCodePanelProps) {
  const [code, setCode] = useState(appliedCode ?? "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const preview = useMemo(() => {
    if (!appliedCode) return null;
    if (appliedCode.toUpperCase() !== TG50_CODE) return { discount: 0, note: messages.cart.promoUnknown };
    if (subtotal < TG50_MIN) {
      return { discount: 0, note: messages.cart.promoTg50UnderMin(TG50_MIN) };
    }
    return { discount: TG50_VALUE, note: messages.cart.promoTg50Applied(TG50_VALUE) };
  }, [appliedCode, subtotal]);

  async function handleApply() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await onApply(code.trim());
      setMessage(result.message);
      if (result.ok) {
        setCode(code.trim().toUpperCase());
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
      <p className="text-sm font-semibold">{messages.cart.promoTitle}</p>
      <p className="mt-1 text-xs opacity-70">{messages.cart.promoHint}</p>
      {appliedCode ? (
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium">{appliedCode}</p>
            {preview ? <p className="text-xs opacity-80">{preview.note}</p> : null}
          </div>
          <AppButton variant="secondary" disabled={disabled || busy} onClick={onRemove}>
            {messages.cart.promoRemove}
          </AppButton>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={code}
            disabled={disabled || busy}
            onChange={(e) => setCode(e.target.value)}
            placeholder={messages.cart.promoPlaceholder}
            className="min-h-11 flex-1 rounded-[12px] border border-black/10 bg-[var(--tg-theme-bg-color,#fff)] px-3 text-sm uppercase"
            autoCapitalize="characters"
          />
          <AppButton disabled={disabled || busy || !code.trim()} onClick={() => void handleApply()}>
            {messages.cart.promoApply}
          </AppButton>
        </div>
      )}
      {message ? <p className="mt-2 text-xs opacity-80">{message}</p> : null}
    </div>
  );
}

export function computeTg50Discount(subtotal: number, code: string | null): number {
  if (!code || code.toUpperCase() !== TG50_CODE) return 0;
  if (subtotal < TG50_MIN) return 0;
  return TG50_VALUE;
}
